import * as React from "react";
import {FC, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import Page from "../../layout/Page";
import PageHeader from "../../layout/PageHeader";
import PageContent from "../../layout/PageContent";
import {OrdersList} from "../shop/components/OrdersList";
import {
   EnumIntegrationConnectorMessages,
   EnumIntegrationInstanceRefType,
   OrderFragment,
   useBrandOrdersPageSelectedOrdersLazyQuery,
   useBrandOrdersPageShopsQuery,
   useBrandOrdersPageSynchronizeMutation,
   useOrderIntegrationExecuteMutation
} from "../../Queries";
import {OrderPagePath} from "../shop/orders/OrderPage";
import {useParams} from "react-router";
import {ParamBrandHouse} from "../../Parameters";
import {ViewFilter} from "../../components/ViewFilter";
import {FlexCommand} from "../../components/flexlist/FlexTypes";
import {SettingsRemote, Sync} from "@mui/icons-material";
import {Dialogs} from "../../DialogProvider";
import {SavingUI} from "../../SavingProvider";
import {Button, LinearProgress} from "@mui/material";
import {useRecoilValue} from "recoil";
import {CurrentUserIsAdmin} from "../../atoms/CurrentUser";
import {RangeCommand} from "../../components/rangelist/RangeListTypes";

export const BrandOrdersPagePath = (brandHouseId: string) => {
   return "/brands/" + brandHouseId + "/orders"
}

export const BrandOrdersPage: FC<any> = () => {
   const {brandHouseId} = useParams<ParamBrandHouse>()
   const navigate = useNavigate();
   const [shopIds, setShopIds] = React.useState<string[]>([]);
   const isAdministrator = useRecoilValue(CurrentUserIsAdmin);

   // First query to get shops
   const {loading: shopsLoading} = useBrandOrdersPageShopsQuery({
      variables: {
         brandHouseId: brandHouseId!,
         instanceFilter: undefined
      },
      skip: !brandHouseId,
      onCompleted: (result: any) => {
         setShopIds(result.ShopMany?.map((shop:any) => shop._id) || []);
      }
   });

   // Build the instance filter once we have shop IDs
   const instanceFilter = useMemo(() => {
      const orConditions = [
         {refType: EnumIntegrationInstanceRefType.LOGISTICSPARTNER}
      ];
      for (const shopId of shopIds) {
         orConditions.push({
            refId: shopId,
            refType: EnumIntegrationInstanceRefType.SHOP
         } as any);
      }
      return {OR: orConditions};
   }, [shopIds]);

   // Second query with the built filter (only after we have shop IDs)
   const {data, loading: instancesLoading} = useBrandOrdersPageShopsQuery({
      variables: {
         brandHouseId: brandHouseId!,
         instanceFilter
      },
      skip: !brandHouseId || shopIds.length === 0
   });

   const [executeIntegration] = useOrderIntegrationExecuteMutation();
   const [synchronize] = useBrandOrdersPageSynchronizeMutation();
   const [fetchSelectedOrders] = useBrandOrdersPageSelectedOrdersLazyQuery();

   const onSynchronizeOrders = async () => {
      const shops = data?.ShopMany || [];
      await SavingUI.process(async () => {
         for (const shop of shops) {
            await synchronize({
               variables: {shopId: shop._id, service: 'orders'},
               fetchPolicy: 'no-cache'
            });
         }
      }, 'Synchronizing orders');
   }

   const extraCommands: RangeCommand<OrderFragment>[] = useMemo(() => {
      if (!isAdministrator || !data?.IntegrationInstances?.length) {
         return [];
      }

      const integrationCommand: RangeCommand<OrderFragment> = {
         id: 'integrations',
         title: 'Integrations',
         icon: SettingsRemote,
         children: []
      };

      for (const instance of data.IntegrationInstances || []) {
         const messages = instance.connector?.messages || [];
         if (!messages.includes(EnumIntegrationConnectorMessages.CREATEORDER) &&
             !messages.includes(EnumIntegrationConnectorMessages.UPDATEORDER)) {
            continue;
         }
         integrationCommand.children!.push({
            id: 'integration_' + instance._id,
            title: 'Send \'UPDATEORDER\' to ' + instance.name,
            icon: SettingsRemote,
            execute: async (orderIds) => {
               Dialogs.confirm({
                  title: 'Update ' + instance.name + ' for selected orders?',
                  subtitle: 'This will trigger integration for selected orders.'
               }, async () => {
                  await SavingUI.process(async () => {
                     const result = await fetchSelectedOrders({
                        variables: {
                           orderIds
                        },
                        fetchPolicy: 'no-cache'
                     });
                     const ordersById = new Map(
                        (result.data?.OrderPagination?.items || [])
                           .map((order) => [String(order._id), order])
                     );

                     for (const orderId of orderIds) {
                        const order = ordersById.get(orderId);
                        if (!order) {
                           throw new Error('Unable to load selected order ' + orderId);
                        }

                        await executeIntegration({
                           variables: {
                              refId: order.shopId,
                              arguments: [order._id, "", instance._id]
                           }
                        });
                     }
                  }, 'Done', 'Updating .. please wait');
               });
            }
         });
      }

      if (integrationCommand.children!.length === 0) {
         return [];
      }

      return [integrationCommand];
   }, [isAdministrator, data, executeIntegration, fetchSelectedOrders]);

   const onSelectRow = (row: OrderFragment, newWindow?: boolean) => {
      if (newWindow) {
         window.open(OrderPagePath(row.shop?.brandHouseId, row.shopId, row._id), '_blank', '')
      } else {
         navigate(OrderPagePath(row.shop?.brandHouseId, row.shopId, row._id));
      }
   }

   if (shopsLoading || instancesLoading) {
      return <Page>
         <PageHeader title={"Orders"}/>
         <PageContent fullHeight>
            <LinearProgress/>
         </PageContent>
      </Page>;
   }

   return <Page>
      <PageHeader title={"Orders"}>
         <Button
            variant={"outlined"}
            onClick={onSynchronizeOrders}
            startIcon={<Sync/>}
         >
            Synchronize
         </Button>
      </PageHeader>
      <PageContent fullHeight>
         <ViewFilter dataType="orders">
            {(viewId) => (
               <OrdersList
                  listId={"brandOrders"}
                  brandHouseId={brandHouseId}
                  onSelectOrder={onSelectRow}
                  extraCommands={extraCommands}
                  viewId={viewId}
               />
            )}
         </ViewFilter>
      </PageContent>
   </Page>
}
